import AsyncStorage from '@react-native-async-storage/async-storage';
import { isDevHubClient } from './devHub';
import { reloadApp } from './reloadApp';

export type DevApiTarget = {
  url: string;
  identifier: string;
  name: string;
};

/** Dev hub (INDEX 0): API base URL + organization identifier header pairs. */
export const DEV_API_TARGETS: DevApiTarget[] = [
  { url: 'https://stagingfoodna.dietmaster.fit/api/customer/', identifier: 'lofat', name: 'Lofat Staging' },
  { url: 'https://stagingfoodna.dietmaster.fit/api/customer/', identifier: 'ghadhana', name: 'Ghadana Staging' },
  { url: 'https://foodna.dietmaster.fit/api/customer/', identifier: 'ghadhana', name: 'Ghadana Production' },
  { url: 'https://foodna.dietmaster.fit/api/customer/', identifier: 'lofat', name: 'Lofat Production' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'testing', name: 'Eataami Console' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'abietadm', name: 'Abieta' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'xiv', name: 'XIV' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'dietcircledm', name: 'Diet Circle' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'eatsmart', name: 'Eatsmart' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'nutrilifedm', name: 'Nutrilife' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: '86healthy', name: '86 Healthy' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'prepdiet', name: 'Prep Diet' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'ingest', name: 'Ingest' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'notchdiet', name: 'Notch Diet' },
  { url: 'https://console.dietmaster.fit/api/customer/', identifier: 'diettime', name: 'Diet Time' },
  { url: 'https://staging.dietmaster.fit/api/customer/', identifier: 'staging', name: 'Eataami Staging' },
];

const DEV_API_TARGET_STORAGE_KEY = '@dev_api_target_selection';

function devTargetKey(target: DevApiTarget): string {
  return `${target.url}::${target.identifier}`;
}

function findDevApiTarget(selection: string | null | undefined): DevApiTarget | null {
  if (!selection) {
    return null;
  }
  return DEV_API_TARGETS.find(target => devTargetKey(target) === selection) ?? null;
}

export function getDefaultDevApiTarget(): DevApiTarget {
  return (
    DEV_API_TARGETS[0] ?? {
      url: 'https://console.dietmaster.fit/api/customer/',
      identifier: 'testing',
      name: 'Default',
    }
  );
}

export function getDevApiTargetOptions(): DevApiTarget[] {
  return DEV_API_TARGETS;
}

export function applyDevApiTarget(target: DevApiTarget): void {
  const { environment } = require('../../shared/config/environment') as typeof import('../../shared/config/environment');
  environment.API_URL = target.url;
  environment.IDENTIFIER = target.identifier;
  environment.IS_INTERNAL = true;
}

/** Call during app boot before any API requests (INDEX 0 only). */
export async function resolveDevEnvironment(): Promise<void> {
  if (!isDevHubClient()) {
    return;
  }

  let target = getDefaultDevApiTarget();
  try {
    const stored = await AsyncStorage.getItem(DEV_API_TARGET_STORAGE_KEY);
    const resolved = findDevApiTarget(stored);
    if (resolved) {
      target = resolved;
    }
  } catch {
    // non-fatal
  }

  applyDevApiTarget(target);
}

/** Dev hub: persist API target, log out, and reload JS to apply. */
export async function setDevApiTarget(target: DevApiTarget): Promise<void> {
  if (!isDevHubClient()) {
    return;
  }

  const { environment } = require('../../shared/config/environment') as typeof import('../../shared/config/environment');

  const is_known = DEV_API_TARGETS.some(
    entry => entry.url === target.url && entry.identifier === target.identifier,
  );
  if (
    !is_known ||
    (target.url === environment.API_URL && target.identifier === environment.IDENTIFIER)
  ) {
    return;
  }

  try {
    await AsyncStorage.setItem(DEV_API_TARGET_STORAGE_KEY, devTargetKey(target));
  } catch {
    // best-effort
  }

  try {
    const { clearSessionForDevHubApiTargetSwitch } = require('../../shared/auth/authSessionApi') as typeof import('../../shared/auth/authSessionApi');
    await clearSessionForDevHubApiTargetSwitch();
  } catch {
    // best-effort — still reload so the new target applies on boot
  }

  reloadApp();
}
